import React, { useEffect, useMemo, useState } from 'react';
import { ClientLocation } from 'constants/interface';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanLocationsAction,
  getClientLocationsListAction,
  isLoadingLocationsSelector,
  locationsEntitiesSelector,
  locationsIdsSelector,
} from 'state/slices/clients';
import { Select, Spinner } from 'UI';
import { FormItemProps } from 'antd';

import {
  RemoteSelectContent,
  RemoteSelectSpinnerContainer,
} from './style';

interface LocationSelectSearchProps {
  clientId?: string
  onSelect?: (location?: ClientLocation) => void
  onClear?: () => void
  form?: {
    FormItem: React.ComponentType<FormItemProps>
    formItemProps: FormItemProps
  }
  initialSelected?: ClientLocation
  isDisabled?: boolean
}

const LocationSelectSearch = ({
  clientId,
  onSelect = () => {},
  onClear = () => {},
  form,
  initialSelected,
  isDisabled: externalDisabled = false,
}: LocationSelectSearchProps): JSX.Element => {
  const dispatch = useDispatch();

  const locationsEntities = useSelector(locationsEntitiesSelector);
  const locationsIds = useSelector(locationsIdsSelector);
  const isLoadingLocations = useSelector(isLoadingLocationsSelector);
  const locations = locationsIds
    .map((locationId) => locationsEntities[locationId])
    .filter((location) => !!location) as ClientLocation[];

  const [
    selectedLocation,
    setSelectedLocation,
  ] = useState<ClientLocation | undefined>(initialSelected);
  const [isDropdownOpened, setDropdownOpened] = useState<boolean>(false);

  const memoizedInitialSelected = useMemo(() => (
    initialSelected?.id
      ? ({
        ...initialSelected,
      }) : undefined
  ), [initialSelected?.id]);

  useEffect(() => {
    setSelectedLocation(memoizedInitialSelected);
  }, [memoizedInitialSelected]);

  useEffect(() => {
    dispatch(cleanLocationsAction());
    setSelectedLocation(undefined);
    onClear();
    if (clientId) {
      dispatch(getClientLocationsListAction({
        clientId,
        paginationData: {
          isPaginated: false,
        },
      }));
    }
  }, [clientId]);

  const selectedLocationOption = selectedLocation
    ? [selectedLocation]
    : [];

  const selectItems = isDropdownOpened
    ? locations
    : selectedLocationOption;

  const selectElement = (
    <Select
      placeholder="Selecione uma localização"
      disabled={(!clientId) || isLoadingLocations || externalDisabled}
      showArrow={false}
      onDropdownVisibleChange={(isOpen) => setDropdownOpened(isOpen)}
      onSelect={(locationId) => {
        const curSelectedLocation = locationsEntities[locationId];
        if (curSelectedLocation) {
          setSelectedLocation(curSelectedLocation);
          onSelect(curSelectedLocation);
        }
      }}
      notFoundContent={<div>Nenhuma localização disponível</div>}
      dropdownRender={(menu) => (
        <div>
          <RemoteSelectContent>
            {menu}
            {(isLoadingLocations) && (
              <RemoteSelectSpinnerContainer>
                <Spinner />
              </RemoteSelectSpinnerContainer>
            )}
          </RemoteSelectContent>
        </div>
      )}
    >
      {selectItems.map((locationOption) => (
        <Select.Option
          value={locationOption.id}
          key={locationOption.id}
        >
          {locationOption.name}
        </Select.Option>
      ))}
    </Select>
  );

  return (
    form ? (
      <form.FormItem
        {...form.formItemProps}
      >
        { selectElement }
      </form.FormItem>
    ) : selectElement
  );
};

export default LocationSelectSearch;
